import { type ReactNode, useEffect, useId, useState } from "react";
import { groupDigits, parseNumber } from "@/plan-logic/lib/format";

/**
 * Form controls in the prototype's markup (`.field`, `.pre`, `.choice`, `.ynb`), shared by
 * onboarding, Update Data, Sharpen and the check-in. Values are numbers or undefined (blank).
 */

export function FieldError({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <p className="wp-form-err" role="alert">
      {error}
    </p>
  );
}

/** "required" tag beside a label. */
export function Req() {
  return <span className="req">required</span>;
}

/** The prototype's (i) tooltip. */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span
      className="inf"
      tabIndex={0}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      role="note"
    >
      i<span className="tip">{children}</span>
    </span>
  );
}

/** Where to find your interest rate (the prototype's RATE_TIP), on every rate field. */
export function RateTip() {
  return (
    <InfoTip label="Where to find your interest rate">
      <b>Where to find your rate</b>
      1. Sign in to the bank or lender that holds it.
      <br />
      2. Open the account and look for your most recent statement, or a tab called Account details.
      <br />
      3. The rate is listed as the annual interest rate or APR. For a mortgage it is beside your
      term and amortization.
      <br />
      <br />
      Still cannot find it? Leave it blank and Abi estimates it for now. Fill it in from Update Data
      when you can: a real rate makes the plan sharper.
    </InfoTip>
  );
}

/** A number input that keeps what the user typed while focused and formats it on blur. */
function useNumberText(value: number | undefined, format: (v: number | undefined) => string) {
  const [text, setText] = useState(format(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setText(format(value));
  }, [value, focused, format]);
  return { text, setText, setFocused };
}

type NumberProps = {
  label: ReactNode;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
  error?: string;
  hint?: ReactNode;
  testId?: string;
  required?: boolean;
  style?: React.CSSProperties;
  ariaLabel?: string;
};

const fmtMoney = (v: number | undefined) => groupDigits(v);
const fmtPlain = (v: number | undefined) => (v === undefined ? "" : String(v));
const fmtRate = (v: number | undefined) => (v === undefined ? "" : `${+v.toFixed(3)}%`);

function NumberInput({
  value,
  onChange,
  placeholder,
  testId,
  format,
  ariaLabel,
  invalid,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  placeholder?: string;
  testId?: string;
  format: (v: number | undefined) => string;
  ariaLabel?: string;
  invalid?: boolean;
}) {
  const { text, setText, setFocused } = useNumberText(value, format);
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      data-testid={testId}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        setText(format(parseNumber(text)));
      }}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseNumber(e.target.value));
      }}
    />
  );
}

/** "$" prefixed amount (`.pre`). */
export function MoneyField(p: NumberProps) {
  return (
    <div className="field" style={p.style}>
      <label>
        {p.label} {p.required && <Req />}
      </label>
      <div className="pre">
        <NumberInput
          value={p.value}
          onChange={p.onChange}
          placeholder={p.placeholder}
          testId={p.testId}
          format={fmtMoney}
          ariaLabel={p.ariaLabel ?? (typeof p.label === "string" ? p.label : undefined)}
          invalid={!!p.error}
        />
      </div>
      {p.hint && <p className="hint">{p.hint}</p>}
      <FieldError error={p.error} />
    </div>
  );
}

/** The placeholder of a rate that may be left blank: blank is sent as not known, never 0%. */
export const RATE_UNKNOWN = "Not sure? We'll estimate it";

/** Interest rate, percent, with the "where to find it" tip. */
export function RateField(p: NumberProps) {
  return (
    <div className="field" style={p.style}>
      <label>
        {p.label} {p.required !== false && <Req />}
        <RateTip />
      </label>
      <NumberInput
        value={p.value}
        onChange={p.onChange}
        placeholder={p.placeholder ?? "e.g. 4.79%"}
        testId={p.testId}
        format={fmtRate}
        ariaLabel={p.ariaLabel ?? (typeof p.label === "string" ? p.label : undefined)}
        invalid={!!p.error}
      />
      {p.hint && <p className="hint">{p.hint}</p>}
      <FieldError error={p.error} />
    </div>
  );
}

/** A free-text field (names). */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
  testId,
  style,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testId?: string;
  style?: React.CSSProperties;
}) {
  const id = useId();
  return (
    <div className="field" style={style}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function NumberField(p: NumberProps) {
  return (
    <div className="field" style={p.style}>
      <label>
        {p.label} {p.required && <Req />}
      </label>
      <NumberInput
        value={p.value}
        onChange={p.onChange}
        placeholder={p.placeholder}
        testId={p.testId}
        format={fmtPlain}
        ariaLabel={p.ariaLabel ?? (typeof p.label === "string" ? p.label : undefined)}
        invalid={!!p.error}
      />
      {p.hint && <p className="hint">{p.hint}</p>}
      <FieldError error={p.error} />
    </div>
  );
}

export function SelectField<T extends string | number>({
  label,
  value,
  options,
  onChange,
  testId,
  style,
  error,
  hint,
}: {
  label: ReactNode;
  value: T;
  options: [T, string][];
  onChange: (v: T) => void;
  testId?: string;
  style?: React.CSSProperties;
  error?: string;
  hint?: ReactNode;
}) {
  const numeric = typeof value === "number";
  return (
    <div className="field" style={style}>
      <label>{label}</label>
      <select
        value={String(value)}
        data-testid={testId}
        aria-label={typeof label === "string" ? label : undefined}
        onChange={(e) => onChange((numeric ? Number(e.target.value) : e.target.value) as T)}
      >
        {options.map(([v, l]) => (
          <option key={String(v)} value={String(v)}>
            {l}
          </option>
        ))}
      </select>
      {hint && <p className="hint">{hint}</p>}
      <FieldError error={error} />
    </div>
  );
}

/** A large choice button (`.choice`). */
export function Choice({
  selected,
  onClick,
  icon,
  title,
  tag,
  children,
  disabled,
  testId,
}: {
  selected: boolean;
  onClick?: () => void;
  icon: ReactNode;
  title: ReactNode;
  tag?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={`choice${selected ? " sel" : ""}${disabled ? " off" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      data-testid={testId}
    >
      <span className="ic">{icon}</span>
      <span>
        <b>
          {title}
          {tag && <span className="tag">{tag}</span>}
        </b>
        {children && <p>{children}</p>}
      </span>
    </button>
  );
}

/** Yes / No buttons (`.yn`). */
export function YesNo({
  value,
  onChange,
  yes = "Yes",
  no = "No",
  label,
  testId,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  yes?: string;
  no?: string;
  /** The question, for screen readers, when it isn't the group's heading. */
  label?: string;
  testId?: string;
}) {
  return (
    <div className="yn" role="group" aria-label={label} data-testid={testId}>
      <button
        type="button"
        className={`ynb${value ? " sel" : ""}`}
        aria-pressed={value}
        onClick={() => onChange(true)}
      >
        {yes}
      </button>
      <button
        type="button"
        className={`ynb${!value ? " sel" : ""}`}
        aria-pressed={!value}
        onClick={() => onChange(false)}
      >
        {no}
      </button>
    </div>
  );
}

/** 1-10 importance select with the "share of your spare money" hint (2^level weighting). */
export function ImportanceSelect({
  value,
  onChange,
  share,
  error,
  testId,
  label = "How much does it matter?",
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  share?: number;
  error?: string;
  testId?: string;
  label?: string;
}) {
  return (
    <div className="field" style={{ margin: 0 }}>
      <label>
        {label} <Req />
      </label>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        aria-label="How much this matters, 1 to 10"
        data-testid={testId}
      >
        <option value="">Pick 1 to 10</option>
        {Array.from({ length: 10 }, (_, k) => 10 - k).map((v) => (
          <option key={v} value={v}>
            {v}
            {v === 10 ? " - matters most" : v === 1 ? " - matters least" : ""}
          </option>
        ))}
      </select>
      {share === undefined ? (
        <p className="hint">Ties are fine.</p>
      ) : (
        <p className="hint">
          About <b>{Math.round(share * 100)}%</b> of your spare money goes here.
        </p>
      )}
      <FieldError error={error} />
    </div>
  );
}
